import React, { useState, useEffect, useCallback } from "react";
import { Box, Typography, Fade, Stack } from "@mui/material";
import { useTranslation } from "react-i18next";
import liff from "@line/liff";
import { useNavigate } from "react-router-dom";
import { useBasicInfo } from "works/higurashi/hooks/useBasicInfo";
import LockIcon from "@mui/icons-material/Lock";

import Loading from "default/components/Loading";
import Header from "works/higurashi/components/Header";
import AppLayout from "works/higurashi/components/AppLayout";
import Error from "default/components/Error";
import TicketNotFound from "default/components/TicketNotFound";
import StyledButton from "default/components/StyledButton";
import ImageWithSaveButton from "default/components/ImageWithSaveButton";
import PhaseTitle from "works/higurashi/components/PhaseTitle";
import NextPhaseSection from "works/higurashi/components/NextPhaseSection";
import ResetMemorySection from "works/higurashi/components/ResetMemorySection";
import OtherCharactersButton from "works/higurashi/components/OtherCharactersButton";

import useMyPageData from "works/higurashi/hooks/useMyPageData";
import useNextPhase from "works/higurashi/hooks/useNextPhase";
import useResetMemory from "hooks/useResetMemory";

import { listQuests } from "works/higurashi/utils/api";
import { getOtherHigurashiSouls } from "works/higurashi/utils/characters";

// Component for individual quest card
const QuestCard = ({ quest, index, delay = 0 }) => {
  const getBoxShadow = () => {
    if (quest.isLocked) {
      return "0 0 12px rgba(191, 33, 32, 0.55)";
    }
    return quest.isCleared ? "0 0 10px #FFFFFF" : "0 0 10px #C32120";
  };

  return (
    <Fade in timeout={800} style={{ transitionDelay: `${delay}ms` }}>
      <Box
        sx={{
          width: "72%",
          maxWidth: 600,
          borderRadius: 2,
          padding: 3,
          marginBottom: 3,
          boxShadow: getBoxShadow(),
          background: quest.isLocked ? "rgba(0, 0, 0, 0.45)" : "transparent",
          backdropFilter: quest.isLocked
            ? "blur(6px) saturate(0.7)"
            : "none",
          WebkitBackdropFilter: quest.isLocked
            ? "blur(6px) saturate(0.7)"
            : "none",
          transition: "all 0.3s ease",
          position: "relative",
          overflow: "hidden",
          "&:hover": {
            boxShadow: getBoxShadow(),
          },
        }}
      >
        {/* Quest Content - blurred if locked, hidden text */}
        <Box
          sx={{
            filter: quest.isLocked ? "blur(8px)" : "none",
            opacity: quest.isLocked ? 0.35 : 1,
            transition: "filter 0.3s ease, opacity 0.3s ease",
            pointerEvents: quest.isLocked ? "none" : "auto",
          }}
        >
          {/* Quest Name */}
          <Typography
            sx={(theme) => ({
              fontFamily: theme.system.fonts.body,
              fontSize: "20px",
              marginBottom: 1,
              fontWeight: 800,
              letterSpacing: 1.5,
              textAlign: "center",
            })}
          >
            {quest.name}
          </Typography>

          {/* Quest Description */}
          <Typography
            sx={{
              fontSize: "14px",
              lineHeight: 1.8,
              textAlign: "center",
              padding: "0 8px",
              whiteSpace: "pre-line",
            }}
          >
            {quest.description}
          </Typography>
        </Box>

        {/* Lock overlay for locked quests */}
        {quest.isLocked && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 2,
              zIndex: 1,
            }}
          >
            <LockIcon
              sx={{
                color: "rgba(255, 255, 255, 0.85)",
                fontSize: 28,
                filter: "drop-shadow(0 0 6px rgba(191, 33, 32, 0.6))",
              }}
            />
          </Box>
        )}
      </Box>
    </Fade>
  );
};

const getRewardImage = (visualStyle) => {
  const style = visualStyle === "cs" ? "cs" : "original";
  return {
    url: `/higurashi/quests/onikakushi_reward_${style}.jpg`,
    fileName: `onikakushi_reward_${style}.jpg`,
  };
};

const useQuests = (accountKey, enabled = true) => {
  const [state, setState] = useState({
    quests: [],
    isLoading: enabled,
    isError: false,
  });

  const loadQuests = useCallback(async () => {
    if (!enabled) {
      setState((prev) => ({ ...prev, isLoading: false, isError: false }));
      return;
    }

    if (!accountKey) {
      setState((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    try {
      setState((prev) => ({ ...prev, isLoading: true, isError: false }));
      const quests = await listQuests(accountKey);
      setState((prev) => ({ ...prev, quests, isError: false }));
    } catch (error) {
      console.error("Error loading quests:", error);
      setState((prev) => ({ ...prev, isError: true }));
    } finally {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, [accountKey, enabled]);

  useEffect(() => {
    loadQuests();
  }, [loadQuests]);

  return { ...state, refetch: loadQuests };
};

// Main Component
function Quests() {
  const navigate = useNavigate();
  const {
    work,
    chapter,
    accountKey,
    lng,
    character,
    visualStyle,
    state: basicState,
  } =
    useBasicInfo();

  const { t } = useTranslation([work, "common"]);
  const [showPrevious, setShowPrevious] = React.useState(false);
  const otherSouls = getOtherHigurashiSouls({ character, visualStyle, lng });

  const handleCloseWindow = () => {
    liff.closeWindow();
  };

  const handleRequestJudgement = () => {
    window.open(
      "https://docs.google.com/forms/d/e/1FAIpQLSckxdz-E1hLeEAwtCSYx5zoSNq6s-tDAibm7uEu3xMNDhMI8g/viewform",
      "_blank"
    );
  };

  const handleTogglePrevious = () => {
    setShowPrevious(!showPrevious);
  };

  const handleNavigateHowToUse = () => {
    const queryParams = new URLSearchParams();
    if (accountKey) queryParams.set("account", accountKey);
    if (lng) queryParams.set("lng", lng);
    const queryString = queryParams.toString();
    navigate(
      `/${work || "higurashi"}/how-to-use${queryString ? `?${queryString}` : ""}`
    );
  };

  const handleNavigateHints = () => {
    const queryParams = new URLSearchParams();
    if (accountKey) queryParams.set("account", accountKey);
    if (lng) queryParams.set("lng", lng);
    const queryString = queryParams.toString();
    navigate(
      `/${work || "higurashi"}/hints${queryString ? `?${queryString}` : ""}`
    );
  };

  const handleNavigateTickets = () => {
    const queryParams = new URLSearchParams();
    if (accountKey) queryParams.set("account", accountKey);
    if (lng) queryParams.set("lng", lng);
    const queryString = queryParams.toString();
    navigate(
      `/${work || "higurashi"}/tickets${queryString ? `?${queryString}` : ""}`
    );
  };

  const {
    data: myPageData,
    isLoading: isMyPageLoading,
    isError: isMyPageError,
    canProceedToNextPhase,
    nextPhaseControl,
    canReset,
    isExpired,
    refetch: refetchMyPageData,
  } = useMyPageData(accountKey);
  const hasState = Boolean(myPageData);
  const isEnded = myPageData?.is_ended === true;
  const resetMemory = useResetMemory(canReset, isExpired);
  const {
    quests,
    isLoading: isQuestsLoading,
    isError: isQuestsError,
    refetch: refetchQuests,
  } = useQuests(accountKey, !isMyPageLoading && hasState && !isEnded);
  const onAdvanced = useCallback(async () => {
    await Promise.all([refetchMyPageData(), refetchQuests()]);
  }, [refetchMyPageData, refetchQuests]);
  const nextPhase = useNextPhase(accountKey, onAdvanced);
  const currentStory = myPageData?.stories?.current;
  const currentPhase = currentStory?.phase || basicState?.phase;
  const currentStoryOrder = Number(currentStory?.order);
  const currentQuestOrder = Number(quests?.current?.phase?.order);
  const isPhaseOne =
    String(currentPhase) === "1" ||
    currentStoryOrder === 1 ||
    currentQuestOrder === 1;
  const rewardImage = getRewardImage(visualStyle);
  const canShowManualAdvanceButton =
    isPhaseOne && canProceedToNextPhase && Boolean(nextPhaseControl);
  const hasPreviousQuests =
    quests?.previous?.some((item) => item.quests.length > 0) || false;
  const canShowPreviousQuestsButton = !isPhaseOne && hasPreviousQuests;
  const hasQuestData = Boolean(quests?.current);

  // Render loading state
  if (isMyPageLoading || (hasState && !isEnded && isQuestsLoading)) {
    return (
      <AppLayout page="Quests" width="100%">
        <Loading />
      </AppLayout>
    );
  }

  // Render error state
  if (isMyPageError || (hasState && !isEnded && isQuestsError)) {
    return (
      <AppLayout page="Quests" width="100%">
        <Error />
      </AppLayout>
    );
  }

  if (!hasState) {
    return (
      <AppLayout page="Quests" width="100%">
        <TicketNotFound onClick={handleNavigateTickets} />
      </AppLayout>
    );
  }

  if (isEnded) {
    return (
      <AppLayout page="Quests" width="100%">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 5,
            width: "100%",
            mb: 5,
            minHeight: "100vh",
            paddingTop: 4,
            paddingBottom: 4,
          }}
        >
          <Fade in timeout={600}>
            <Box
              sx={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <Header
                title="Reward"
                highlightedWord="R"
                subtitle={t(`chapter.${chapter}`)}
              />
            </Box>
          </Fade>

          <Fade in timeout={800}>
            <Box
              sx={{
                width: "72%",
                maxWidth: 600,
                display: "flex",
                justifyContent: "center",
              }}
            >
              <ImageWithSaveButton
                imageUrls={[rewardImage.url]}
                showDownloadButton={true}
                saveMode="share"
                saveFileName={rewardImage.fileName}
              />
            </Box>
          </Fade>

          <Fade in timeout={900} style={{ transitionDelay: "100ms" }}>
            <Stack
              spacing={4}
              alignItems="center"
              sx={{
                width: "72%",
                maxWidth: 600,
              }}
            >
              {isExpired && (
                <StyledButton variant="contained" onClick={handleNavigateTickets}>
                  {t("common:button.purchase_episode")}
                </StyledButton>
              )}

              <ResetMemorySection
                resetMemory={resetMemory}
                t={t}
                buttonLabel={t("higurashi:button.restart")}
                highlight
                mode="restart"
              />
            </Stack>
          </Fade>

          <Fade in timeout={1000} style={{ transitionDelay: "200ms" }}>
            <Stack
              spacing={4}
              alignItems="center"
              sx={{
                width: "72%",
                maxWidth: 600,
              }}
            >
              <StyledButton variant="contained" onClick={handleNavigateHowToUse}>
                {t("common:Footer.how_to_play")}
              </StyledButton>

              <StyledButton variant="contained" onClick={handleCloseWindow}>
                {t("common:button.back_to_chat")}
              </StyledButton>
            </Stack>
          </Fade>
        </Box>
      </AppLayout>
    );
  }

  if (!hasQuestData) {
    return (
      <AppLayout page="Quests" width="100%">
        <Error />
      </AppLayout>
    );
  }

  // Main render
  return (
    <AppLayout page="Quests" width="100%">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 5,
          width: "100%",
          mb: 5,
          minHeight: "100vh",
          paddingTop: 4,
          paddingBottom: 4,
        }}
      >
        {/* Header */}
        <Fade in timeout={600}>
          <Box
            sx={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Header
              title="Quests"
              highlightedWord="Q"
              subtitle={t(`chapter.${chapter}`)}
            />
          </Box>
        </Fade>

        {/* Quests Section */}
        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {/* Current Quests - Always visible */}
          <>
            {/* Phase Title */}
            <Fade in timeout={800}>
              <Box sx={{ mb: 2 }}>
                <PhaseTitle
                  order={quests.current.phase.order}
                  title={quests.current.phase.title}
                  phase={currentPhase}
                />
              </Box>
            </Fade>

            {/* Quest Cards */}
            {quests.current.quests.map((quest, index) => (
              <QuestCard
                key={quest.key}
                quest={quest}
                index={index}
                delay={500 + index * 200} // Staggered animation (adjusted for phase title)
              />
            ))}

            {/* 次の章へ進む */}
            {canShowManualAdvanceButton && (
              <Fade in timeout={600}>
                <Box
                  sx={{
                    width: "72%",
                    maxWidth: 600,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <NextPhaseSection nextPhase={nextPhase} control={nextPhaseControl} />
                </Box>
              </Fade>
            )}
          </>

          {/* Previous Quests - Show when toggled */}
          {canShowPreviousQuestsButton && showPrevious && (
            <Box sx={{ width: "100%", mt: 6 }}>
              {/* Separator */}
              <Fade in timeout={800} style={{ transitionDelay: "300ms" }}>
                <Box sx={{ mb: 4, display: "flex", justifyContent: "center" }}>
                  <Typography
                    sx={{
                      fontSize: "18px",
                      fontWeight: 600,
                      color: "rgba(255, 255, 255, 0.7)",
                      textAlign: "center",
                    }}
                  >
                    ── 過去のクエスト ──
                  </Typography>
                </Box>
              </Fade>

              {quests.previous.map((item, phaseIndex) => (
                <Box key={item.phase.order} sx={{ width: "100%", mb: 4 }}>
                  {/* Phase Title */}
                  <Fade
                    in
                    timeout={800}
                    style={{ transitionDelay: `${600 + phaseIndex * 100}ms` }}
                  >
                    <Box
                      sx={{ mb: 2, display: "flex", justifyContent: "center" }}
                    >
                      <PhaseTitle
                        order={item.phase.order}
                        title={item.phase.title}
                      />
                    </Box>
                  </Fade>

                  {/* Quest Cards for this phase */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                    }}
                  >
                    {item.quests.map((quest, questIndex) => (
                      <QuestCard
                        key={quest.key}
                        quest={quest}
                        index={questIndex}
                        delay={800 + phaseIndex * 300 + questIndex * 150}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        {/* チャットに戻る */}
        <Fade
          in
          timeout={
            showPrevious ? 1000 : 500 + 200 * quests.current.quests.length + 200
          }
          style={{ transitionDelay: "200ms" }}
        >
          <Stack
            spacing={4}
            alignItems="center"
            sx={{
              width: "72%",
              maxWidth: 600,
            }}
          >
            {isExpired && (
              <StyledButton variant="contained" onClick={handleNavigateTickets}>
                {t("common:button.purchase_episode")}
              </StyledButton>
            )}

            <OtherCharactersButton chapter={chapter} souls={otherSouls} />

            <StyledButton variant="contained" onClick={handleNavigateHints}>
              ヒント
            </StyledButton>

            {canShowPreviousQuestsButton && (
              <StyledButton variant="contained" onClick={handleTogglePrevious}>
                過去のクエストを見る
              </StyledButton>
            )}

            <StyledButton variant="contained" onClick={handleRequestJudgement}>
              {t("common:button.request_clear_judgement")}
            </StyledButton>

            <StyledButton variant="contained" onClick={handleCloseWindow}>
              {t("common:button.back_to_chat")}
            </StyledButton>
          </Stack>
        </Fade>
      </Box>
    </AppLayout>
  );
}

export default Quests;
